import { useEffect, useState } from "react";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  clearAll,
  markAllRead,
  useToastHistory,
  type Severity,
  type ToastHistoryEntry,
} from "@/lib/toastHistory";
import { downloadAsPng } from "@/lib/notificationDownload";
import { cn } from "@/lib/utils";

/**
 * Floating notification bell, fixed bottom-right. Click to open a history
 * panel listing every toast (success / warning / error / info) the app has
 * fired in this browser, newest first. Unread count badge persists across
 * page reloads via localStorage. Clicking the bell marks all read.
 *
 * Sits below Sonner's toast stack — App.tsx bumps the Toaster's offset so
 * incoming toasts don't visually collide with this button.
 */
export default function NotificationHistory() {
  const { entries, unreadCount } = useToastHistory();
  const [open, setOpen] = useState(false);

  // Mark-read when the panel opens. We do it in an effect rather than
  // inside the onOpenChange callback so the badge fade-out happens on
  // the same render as the panel appearing, not one tick later.
  useEffect(() => {
    if (open) markAllRead();
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : "Notifications"
          }
          className={cn(
            "fixed bottom-4 right-4 z-40",
            "flex items-center justify-center",
            "w-11 h-11 rounded-full",
            "bg-background border border-border shadow-md",
            "hover:bg-muted transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <Bell className="w-5 h-5 text-foreground" />
          {unreadCount > 0 && (
            <span
              className={cn(
                "absolute -top-1 -right-1",
                "min-w-[1.25rem] h-5 px-1.5 rounded-full",
                "bg-red-600 text-white text-[10px] font-semibold",
                "flex items-center justify-center",
              )}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        sideOffset={12}
        className="w-[22rem] p-0"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {entries.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Trash2 className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
        <div className="max-h-[24rem] overflow-y-auto">
          {entries.length === 0 ? (
            <p className="px-4 py-8 text-sm text-muted-foreground text-center">
              No notifications yet.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {entries.map((entry) => (
                <NotificationRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationRow({ entry }: { entry: ToastHistoryEntry }) {
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <SeverityIcon severity={entry.severity} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground break-words">{entry.message}</p>
        {entry.description && (
          <p className="text-xs text-muted-foreground mt-0.5 break-words">
            {entry.description}
          </p>
        )}
        <p
          className="text-[11px] text-muted-foreground mt-1"
          title={new Date(entry.ts).toLocaleString()}
        >
          {formatRelative(entry.ts)}
        </p>
      </div>
      {/* Per-notification download — produces a sharable PNG of the exact
          message for pasting into chat / issues. Click keeps the popover
          open via Radix's default click-inside behavior. */}
      <button
        type="button"
        onClick={() => {
          void downloadAsPng(entry);
        }}
        title="Download as .png"
        aria-label="Download notification as .png"
        className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      >
        <ImageIcon className="w-3.5 h-3.5" />
      </button>
    </li>
  );
}

function SeverityIcon({ severity }: { severity: Severity }) {
  const className = "w-4 h-4 mt-0.5 shrink-0";
  switch (severity) {
    case "success":
      return <CheckCircle2 className={cn(className, "text-emerald-600")} />;
    case "warning":
      return <AlertTriangle className={cn(className, "text-amber-600")} />;
    case "error":
      return <XCircle className={cn(className, "text-red-600")} />;
    case "info":
    default:
      return <Info className={cn(className, "text-blue-600")} />;
  }
}

/**
 * "2 min ago" / "yesterday" / "Apr 3" style. Cheap rather than precise —
 * the title attribute on the row carries the full timestamp for hover.
 */
function formatRelative(ts: number): string {
  const now = Date.now();
  const deltaSec = Math.max(0, Math.round((now - ts) / 1000));
  if (deltaSec < 60) return "just now";
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr} hr ago`;
  const deltaDay = Math.round(deltaHr / 24);
  if (deltaDay === 1) return "yesterday";
  if (deltaDay < 7) return `${deltaDay} days ago`;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
